import TrackingClient from '../../TrackingClient'
import { EventTracker } from '../EventTracker'
import { Tracker } from '../../types/tracker'
import { UserTraits } from '../../types/traits'
import { SellableProperties } from '../../events/consumer/types'
import * as Events from '../../events'

type FacebookEventParameters =
  | facebook.Pixel.ViewContentParameters
  | facebook.Pixel.SearchParameters
  | facebook.Pixel.AddToCartParameters
  | facebook.Pixel.AddToWishlistParameters
  | facebook.Pixel.InitiateCheckoutParameters
  | facebook.Pixel.AddPaymentInfoParameters
  | facebook.Pixel.PurchaseParameters
  | facebook.Pixel.LeadParameters
  | facebook.Pixel.CompleteRegistrationParameters

type FacebookEvent = {
  eventName: string
  parameters?: FacebookEventParameters
}

export interface FacebookTrackerOptions {
  pixelIds: {
    default: string
    [departmentSlug: string]: string
  }
}

export default class FacebookTracker extends EventTracker implements Tracker {
  private client: TrackingClient
  private options: FacebookTrackerOptions
  private queuedEvents: FacebookEvent[] = []
  private queuedCustomEvents: FacebookEvent[] = []
  private initializedPixelIds = new Set<string>()

  public constructor(client: TrackingClient, options: FacebookTrackerOptions) {
    super()

    this.client = client

    if (!window.fbq) {
      throw new Error(
        'The Facebook Pixel snippet is missing, but must be installed to use the FacebookTracker.'
      )
    }

    this.options = options

    // Initialize the default pixel
    this.initPixel(this.options.pixelIds.default)
  }

  public get fbq() {
    return window.fbq!
  }

  /**
   * Initialize a pixel, ensuring it is only initialized once. The exception to this rule
   *   is when a user's traits have changed, in which case we re-initialize the pixel.
   *
   * The Facebook documentation suggests this is bad practice, or an error, but others
   *   say it's supported and is the only way to add traits to a pixel without refreshing
   *   the page (i.e. in a single page app).
   *
   * See: https://stackoverflow.com/questions/39355191/advanced-matching-with-new-facebook-pixel-in-javascript-web-app
   */
  public initPixel(pixelId: string) {
    if (this.initializedPixelIds.has(pixelId)) {
      return
    }
    this.fbq('init', pixelId)
    this.initializedPixelIds.add(pixelId)
  }

  /**
   * When a user's traits have changed, we must re-initialize the pixel in order to
   *   associate the new traits with that pixel.
   *
   * The Facebook documentation suggests this is bad practice, or an error, but others
   *   say it's supported and is the only way to add traits to a pixel without refreshing
   *   the page (i.e. in a single page app).
   *
   * See: https://stackoverflow.com/questions/39355191/advanced-matching-with-new-facebook-pixel-in-javascript-web-app
   */
  public addTraits(traits: UserTraits) {
    const advancedMatchingData = this.getAdvancedMatchingDataFromUserTraits(traits)

    this.initializedPixelIds.forEach((pixelId) => {
      // @ts-ignore Facebook Pixel typings are incomplete
      this.fbq('init', pixelId, advancedMatchingData)
    })
  }

  private getAdvancedMatchingDataFromUserTraits(traits: UserTraits) {
    // Format date of birth as yyyymmdd
    let formattedDateOfBirth = ''
    if (traits.dateOfBirth) {
      const birthYear = traits.dateOfBirth.getFullYear()
      const birthMonth = traits.dateOfBirth.getMonth()
      const birthDate = traits.dateOfBirth.getDate()

      formattedDateOfBirth += birthYear.toString()
      formattedDateOfBirth += birthMonth < 10 ? `0${birthMonth.toString()}` : birthMonth.toString()
      formattedDateOfBirth += birthDate < 10 ? `0${birthDate.toString()}` : birthDate.toString()
    }

    return {
      ct:
        traits.address &&
        traits.address.city &&
        traits.address.city.toLowerCase().replace(/\s/g, ''),
      // TODO: Normalize state to two-letter state code
      st: traits.address && traits.address.state && traits.address.state.toLowerCase(),
      // TODO: Normalize country to two-letter country code
      country: traits.address && traits.address.country,
      zp: traits.address && traits.address.postalCode,
      db: traits.dateOfBirth && formattedDateOfBirth,
      em: traits.email && traits.email.toLowerCase(),
      fn: traits.firstName && traits.firstName.toLowerCase(),
      ln: traits.lastName && traits.lastName.toLowerCase(),
      ge: traits.gender,
      ph: traits.phone && traits.phone.replace(/[^\d]/g, ''),
    }
  }

  /**
   * Remove the queued events when the user logs out
   */
  public reset() {
    this.queuedEvents = []
  }

  /**
   * A wrapper for the Facebook Pixel tracking API (`fbq`).
   *
   * Events are always tracked to a default pixel ID, and are additionally tracked to a
   *   department-specific pixel ID if the user was associated with a department when
   *   they were acquired. If the department is not known at the time the event is fired
   *   those events are queued in case they are associated with a department later on
   *   (at which point all queued events are flushed to the department-specific pixel)
   */
  private track(eventName: string, parameters?: FacebookEventParameters) {
    // TODO: Track "active" or "acquisition" department
    const departmentSlug = this.client.getDepartment()

    const defaultPixelId = this.options.pixelIds.default
    const departmentPixelId = departmentSlug && this.options.pixelIds[departmentSlug]

    // @ts-ignore Facebook Pixel typings are incomplete
    this.fbq('trackSingle', defaultPixelId, eventName, parameters)

    if (departmentPixelId) {
      // @ts-ignore Facebook Pixel typings are incomplete
      this.fbq('trackSingle', departmentPixelId, eventName, parameters)
    } else {
      this.queuedEvents.push({
        eventName,
        parameters,
      })
    }
  }

  /**
   * A wrapper for Facebook Pixel tracking API custom events.
   * See track function comment above for more details.
   */

  private trackCustomEvent(eventName: string, parameters?: any) {
    const departmentSlug = this.client.getDepartment()

    const defaultPixelId = this.options.pixelIds.default
    const departmentPixelId = departmentSlug && this.options.pixelIds[departmentSlug]

    this.fbq('trackSingleCustom', defaultPixelId, eventName, parameters)

    if (departmentPixelId) {
      this.fbq('trackSingleCustom', departmentPixelId, eventName, parameters)
    } else {
      this.queuedCustomEvents.push({
        eventName,
        parameters,
      })
    }
  }

  /**
   * When the user's department is set for the first time, flush any queued events to
   *   that department's pixel
   */
  public setDepartment(departmentSlug: string) {
    const departmentPixelId = this.options.pixelIds[departmentSlug]

    if (!departmentPixelId) {
      console.warn(`No Facebook Pixel ID configured for ${departmentSlug}`)
    }

    // Initialize the department-specific pixel
    this.initPixel(departmentPixelId)

    let event
    while ((event = this.queuedEvents.shift())) {
      // @ts-ignore Facebook Pixel typings are incomplete
      window.fbq('trackSingle', departmentPixelId, event.eventName, event.parameters)
    }

    while ((event = this.queuedCustomEvents.shift())) {
      // @ts-ignore Facebook Pixel typings are incomplete
      window.fbq('trackSingleCustom', departmentPixelId, event.eventName, event.parameters)
    }
  }

  /**
   * Convert our internal tracking representation of a sellable to the format Facebook
   *   expects.
   *
   * See: https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking#object-properties
   */
  private getFacebookContentsFromSellableTrackingProperties(
    sellable: SellableProperties & { quantity?: number }
  ) {
    return {
      id: sellable.product_id,
      quantity: sellable.quantity || 1,
      item_price: sellable.price || undefined,
    }
  }

  public trackPageViewed() {
    this.track('PageView')
  }

  public trackCheckoutStarted({ properties }: Events.CheckoutStartedEvent) {
    const contents = properties.products.map(this.getFacebookContentsFromSellableTrackingProperties)
    const contentIds = contents.map((content) => content.id)
    const contentCategory =
      properties.products.length > 0 ? properties.products[0].category : undefined

    this.track('InitiateCheckout', {
      content_category: contentCategory,
      content_ids: contentIds,
      // @ts-ignore Facebook Pixel typings are incomplete
      contents,
      currency: properties.currency,
      num_items: contents.length,
      value: parseFloat(properties.value),
    })
  }

  public trackOrderCompleted({ properties }: Events.OrderCompletedEvent) {
    const contents = properties.products.map(this.getFacebookContentsFromSellableTrackingProperties)
    const contentIds = contents.map((content) => content.id)

    this.track('Purchase', {
      content_ids: contentIds,
      // TODO: What should 'content_name' be for a new request?
      // content_name: ,
      content_type: 'product',
      // @ts-ignore Facebook Pixel typings are incomplete
      contents,
      num_items: contents.length,
      value: parseFloat(properties.value),
      currency: properties.currency,
    })
  }

  public trackProductViewed({ properties }: Events.ProductViewedEvent) {
    const content = this.getFacebookContentsFromSellableTrackingProperties(properties)

    this.track('ViewContent', {
      // TODO: Verify this is correct. If we only track parent sellables we may want to use 'product_group'.
      content_type: 'product',
      // @ts-ignore Facebook Pixel typings are incomplete
      contents: [content],
      content_ids: [content.id],
      content_name: properties.name,
      content_category: properties.category,
      currency: properties.currency,
      // TODO: Unclear whether this represents the "value of the user to the business" (per documentation),
      //   or the value of the sellable.
      value: properties.price ? parseFloat(properties.price) : undefined,
    })
  }

  public trackProductAdded({ properties }: Events.ProductAddedEvent) {
    const content = this.getFacebookContentsFromSellableTrackingProperties(properties)

    this.track('AddToCart', {
      content_type: 'product',
      // @ts-ignore Facebook Pixel typings are incomplete
      contents: [content],
      content_ids: [content.id],
      content_name: properties.name,
      content_category: properties.category,
      currency: properties.currency,
      // TODO: Unclear whether this represents the "value of the user to the business" (per documentation),
      //   or the value of the sellable.
      value: properties.price ? parseFloat(properties.price) : undefined,
    })
  }

  public trackProductAddedToWishlist({ properties }: Events.ProductAddedToWishlistEvent) {
    const content = this.getFacebookContentsFromSellableTrackingProperties(properties)

    this.track('AddToWishlist', {
      content_type: 'product',
      // @ts-ignore Facebook Pixel typings are incomplete
      contents: [content],
      content_ids: [content.id],
      content_name: properties.name,
      content_category: properties.category,
      currency: properties.currency,
      // TODO: Unclear whether this represents the "value of the user to the business" (per documentation),
      //   or the value of the sellable.
      value: properties.price ? parseFloat(properties.price) : undefined,
    })
  }

  public trackRequestSubmitted({ properties }: Events.RequestSubmittedEvent) {
    this.track('Lead', {
      content_category: properties.category_slug,
      // TODO: What should 'content_name' be for a new request?
      // content_name: '',
      // TODO: User-level currency tracking
      // currency: '',
      // TODO: Use 'value' for LTV tracking?
      // value
    })
  }

  public trackProductsSearched({ properties }: Events.ProductsSearchedEvent) {
    this.track('Search', {
      // TODO: What should 'content_*' represent for search tracking?
      // content_category,
      // content_ids,
      // contents,
      // currency,
      search_string: properties.query.keywords,
      // TODO: Assign a value to searches
      // value
    })
  }

  public trackViewCurationLtv({ properties }: Events.ViewCurationLtvEvent) {
    this.track('ViewCurationLTV', {
      value: properties.value,
      currency: properties.currency,
      content_category: properties.content_category,
    })
  }

  public trackCuratedListViewed({ properties }: Events.CuratedListViewedEvent) {
    const contents = properties.items.map(this.getFacebookContentsFromSellableTrackingProperties)
    const contentIds = contents.map((content) => content.id)

    this.track('ViewContent', {
      content_ids: contentIds,
      // TODO: What should the 'content_name' be for a curated list?
      // content_name: '',
      content_type: 'product',
      // @ts-ignore Facebook Pixel typings are incomplete
      contents: contents,
      currency: properties.currency,
      content_category: properties.content_category || undefined,
      // TODO: Will this artificially inflate the value of the user if we consider all curated items in the "value"?
      value: contents.reduce((sum, item) => {
        // TODO: 'item_price' is incorrectly typed as a string. parseInt can be removed when the typings are fixed.
        return sum + parseInt(item.item_price || '0', 10)
      }, 0),
    })
  }

  public trackPaymentInfoEntered({ properties }: Events.PaymentInfoEnteredEvent) {
    const contents = properties.products.map(this.getFacebookContentsFromSellableTrackingProperties)
    const contentIds = contents.map((content) => content.id)
    const contentCategory =
      properties.products.length > 0 ? properties.products[0].category : undefined

    this.track('AddPaymentInfo', {
      content_type: 'product',
      content_category: contentCategory,
      content_ids: contentIds,
      // @ts-ignore Facebook Pixel typings are incomplete
      contents: contents,
      currency: properties.currency,
      value: properties.value ? parseFloat(properties.value) : undefined,
    })
  }

  public trackExpertApplicationSubmitted({ properties }: Events.ExpertApplicationSubmittedEvent) {
    this.track('SubmitApplication', {
      content_category: properties.category,
    })
  }

  public trackUserRegistered(event: Events.UserRegisteredEvent) {
    const { firstName, lastName } = event.context.traits
    const contentName = [firstName, lastName].join(' ')

    // @ts-ignore Facebook Pixel typings are incomplete
    this.track('CompleteRegistration', {
      content_name: contentName,
      // @ts-ignore Facebook Pixel typings are inaccurate
      status: true,
      // TODO: User-level currency tracking
      // currency: 'USD',
    })
  }

  public trackArticleViewed(event: Events.ArticleViewedEvent) {
    this.trackCustomEvent('ArticleViewed', event.properties)
  }

  public trackTopListViewed(event: Events.TopListViewedEvent) {
    this.trackCustomEvent('TopListViewed', event.properties)
  }
}
